import React, { useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Modal,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material'
import { toast } from 'react-toastify'
import {
  useGetDealerGroupsQuery,
  useGetSkuListQuery,
  useDealerSaleBulkUpdateMutation,
} from '../../redux/api/SaleIntegrityApi'
import { ISkuItem } from '../../types/SaleIntegrity'
import { borderRadius, padding } from '@mui/system'

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '480px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '16px',
    overflow: 'hidden',
  },
  headerStyle: {
    display: 'flex',
    p: 1.5,
    justifyContent: 'center',
    backgroundColor: '#1976d2',
  },
  content: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  footerStyle: {
    textAlign: 'right',
    padding: '12px 24px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f9f9f9',
  },
  cancelBtn: {
    marginRight: '10px',
    borderRadius: '20px',
    textDecoration: 'none',
  },
  updateAllBtn: {
    borderRadius: '20px',
    textDecoration: 'none',
  },
}

interface BulkUpdateSaleRecordProps {
  open: boolean
  onClose: () => void
  selectedCount: number
  selectedIds: number[]
  onSuccess: () => void
}

export const BulkUpdateSaleRecord = ({
  open,
  onClose,
  selectedCount,
  selectedIds,
  onSuccess,
}: BulkUpdateSaleRecordProps) => {
  const { data: dealerData = [], isLoading: dealerLoading } = useGetDealerGroupsQuery(undefined, {
    skip: !open,
  })

  const [dealerGroupNumber, setDealerGroupNumber] = useState('')
  const [dealerId, setDealerId] = useState('')
  const [sku, setSku] = useState('')

  const { data: skuList = [], isLoading: skuLoading } = useGetSkuListQuery(dealerId, {
    skip: !open || !dealerId,
  })
  const [dealerSaleBulkUpdate, { isLoading }] = useDealerSaleBulkUpdateMutation()

  const selectedGroup = dealerData.find((g) => g.dealerGroupNumber === dealerGroupNumber)
  const dealerList = selectedGroup ? selectedGroup.dealerIdList : []

  const handleDealerGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDealerGroupNumber(e.target.value)
    setDealerId('')
    setSku('')
  }

  const handleDealerIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDealerId(e.target.value)
    setSku('')
  }

  const resetForm = () => {
    setDealerGroupNumber('')
    setDealerId('')
    setSku('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleUpdateAll = async () => {
    try {
      const payload = {
        dealerSalesIds: selectedIds,
        ...(dealerGroupNumber && { dealerGroupNumber }),
        ...(dealerId && { dealerId }),
        ...(sku && { sku }),
      }

      const response = await dealerSaleBulkUpdate(payload).unwrap()

      if (response?.status === 'success') {
        toast.success(response?.message || 'Records updated successfully')
        resetForm()
        onSuccess()
        onClose()
      } else if (response?.status === 'partial') {
        toast.warning(response?.message)
        resetForm()
        onSuccess()
        onClose()
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Bulk update failed')
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={styles.container}>
        {/* Header */}
        <Box sx={styles.headerStyle}>
          <Typography variant='h6' color='white'>
            Bulk Update Records
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={styles.content}>
          <Typography variant='body2' color='text.secondary'>
            Updating <strong>{selectedCount}</strong> selected record
            {selectedCount !== 1 ? 's' : ''}. Only filled fields will be updated.
          </Typography>

          {/* Dealer Group Number */}
          <TextField
            select
            fullWidth
            size='small'
            label='Dealer Group Number'
            value={dealerGroupNumber}
            onChange={handleDealerGroupChange}
            disabled={dealerLoading || isLoading}
            InputProps={{ sx: { borderRadius: '8px' } }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value=''>
              <em>-- Select Dealer Group --</em>
            </MenuItem>
            {dealerData.map((group, i) => (
              <MenuItem key={i} value={group.dealerGroupNumber}>
                {group.dealerGroupNumber}
              </MenuItem>
            ))}
          </TextField>

          {/* Dealer ID */}
          <TextField
            select
            fullWidth
            size='small'
            label='Dealer ID'
            value={dealerId}
            onChange={handleDealerIdChange}
            disabled={!dealerGroupNumber || isLoading}
            InputProps={{ sx: { borderRadius: '8px' } }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value=''>
              <em>{dealerGroupNumber ? '-- Select Dealer ID --' : 'Select Group First'}</em>
            </MenuItem>
            {dealerList.map((item: any, i: number) => (
              <MenuItem key={i} value={item.dealerId}>
                {item.dealerId}
              </MenuItem>
            ))}
          </TextField>

          {/* SKU — loads after Dealer ID is selected */}
          <TextField
            select
            fullWidth
            size='small'
            label='SKU'
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            disabled={!dealerId || skuLoading || isLoading}
            InputProps={{
              sx: { borderRadius: '8px' },
              endAdornment: skuLoading ? (
                <InputAdornment position='end'>
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
            }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value=''>
              <em>
                {!dealerId
                  ? 'Select Dealer ID First'
                  : skuLoading
                  ? 'Loading SKUs...'
                  : skuList.length === 0
                  ? '-- No SKUs available --'
                  : '-- Select SKU --'}
              </em>
            </MenuItem>
            {skuList.map((item: ISkuItem) => (
              <MenuItem key={item.id} value={item.product_sku}>
                {item.product_sku}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Footer */}
        <Box sx={styles.footerStyle}>
          <Button
            onClick={handleClose}
            variant='outlined'
            color='error'
            disabled={isLoading}
            sx={styles.cancelBtn}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAll}
            variant='contained'
            disabled={isLoading || (!dealerGroupNumber && !dealerId && !sku)}
            sx={styles.updateAllBtn}
          >
            {isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Update All'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
